import { markBoot } from "./boot-timing";
import { clipMediaKind } from "./project-document";
import {
  addClip,
  addProjectAudioTrack,
  clearUndo,
  deleteClip,
  deleteProjectIfPristine,
  duplicateClip,
  getClipsByProject,
  getClipsForProject,
  getProject,
  getProjectAudio,
  getProjectDocumentRecords,
  getProjectText,
  getSettings,
  getUndoSnapshot,
  listProjects,
  moveClip,
  removeProjectAudioTrack,
  restoreProjectAudioTrack,
  replaceProjectAudioTake,
  setProjectAudioScript,
  setProjectAudioSpan,
  writeProjectAudioPositions,
  splitProjectAudioTrack,
  saveProjectText,
  setLastOpenedProjectId,
  undoDeleteLastClip,
  updateClipThumbs,
  updateClipTrim,
  updateClipVolumes,
  updateProjectAudioTrack,
  type ClipVolumeSettings,
  type ProjectAudioTrackSettings,
} from "./storage";
import { probeAudioFile } from "./audio-import";
import { placeSpans } from "./audio-spans";
import { frameForDocument, planDocument } from "./document-plan";
import { isLegacyProject, resolveDocument } from "./project-document";
import { estimateExportCacheBytes } from "./export/export-cache";
import { estimateStorageSpace, type StorageSpace } from "./storage-space";
import type { GeneratedThumbs } from "./thumbs";
import {
  NEW_PROJECT_ID,
  countWords,
  effectiveDurationMs,
  projectKind,
  type ClipId,
  type ClipRecord,
  type Project,
  type ProjectAudioRecord,
  type ProjectAudioTrack,
  type ProjectDocument,
  type ProjectId,
  type ProjectKind,
} from "./types";

export interface ProjectSummary extends Project {
  clipCount: number;
  durationMs: number;
  /** First available clip thumbnail, for the project slot background. */
  posterThumb: Blob | null;
  /** Words in the body — the only size that means anything for text. */
  wordCount: number;
  /** First line of the body, for the text-project card subtitle. */
  textPreview: string;
}

export interface ProjectLoaderData {
  project: Project | null;
  /** The project's kind, resolved once so pages never re-derive it. */
  kind: ProjectKind;
  clips: ClipRecord[];
  /** The project's body copy ('' when it has none). */
  text: string;
  /** Background-audio track played under the clips (null when none is set). */
  audio: ProjectAudioRecord | null;
  /**
   * The project as sections of typed elements.
   *
   * Always present, synthesised for a project recorded before elements existed
   * (see `resolveDocument`) — so nothing downstream has to branch on whether a
   * project is "new shape" or not. `documentIsStored` says which it was, and
   * that only matters for deciding whether the flat publish lane is still an
   * honest description of the picture.
   */
  document: ProjectDocument;
  /** False when the document above was synthesised from clips on read. */
  documentIsStored: boolean;
  canUndo: boolean;
  onboardingDismissed: boolean;
  /** True when the one-time Kody Video Plus purchase is unlocked. */
  watermarkRemoved: boolean;
  /**
   * Plus opt-in: keep the Kody mark on exports after purchase (default off).
   */
  keepWatermark: boolean;
  /** Device storage estimate (null when the API is unavailable). */
  storage: StorageSpace | null;
  /** Opt-in: tag new clips with device location. */
  locationTaggingEnabled: boolean;
  error: string | null;
}

export interface HomeLoaderData {
  /** Projects of the requested kind only — one tab's worth. */
  projects: ProjectSummary[];
  /** How many projects exist in each tab, for the tab badges. */
  countsByKind: Record<ProjectKind, number>;

  /** True when the one-time Kody Video Plus purchase is unlocked. */
  plus: boolean;
  /** Home "Watch the tour" card dismissed (first-timer teaser). */
  tourCardDismissed: boolean;
}

/**
 * Every project, in one list.
 *
 * No longer filtered by kind. A project holds video, pictures and cards at once
 * now, so "which type is this" stopped being a property of the project and
 * became a property of each element inside it — and a tab per type was asking a
 * question the model no longer has an answer to.
 *
 * The kinds themselves survive on the RECORDS, because projects made before this
 * still carry one and still open in their own screens. `countsByKind` survives
 * with them, for anything that wants to say what an old project is.
 */
/** What the storage banner needs. Loaded AFTER the cards — see loadHomePage. */
export interface StorageFigures {
  storage: StorageSpace | null;
  exportCacheBytes: number;
}

/**
 * The device's space, asked for separately.
 *
 * These two used to be awaited inside loadHomePage, which meant the project
 * cards waited for them: navigator.storage.estimate() is slow on some devices,
 * and the export-cache total opens every cached render to stat it. They feed a
 * banner that only appears when space runs hot, so nothing about the screen's
 * first paint depends on them.
 */
export async function loadStorageFigures(): Promise<StorageFigures> {
  const [storage, exportCacheBytes] = await Promise.all([
    estimateStorageSpace(),
    estimateExportCacheBytes(),
  ]);
  return { storage, exportCacheBytes };
}

export async function loadHomePage(): Promise<HomeLoaderData> {
  markBoot("library:start");
  // The storage figures are NOT awaited here. They feed a banner that only
  // appears when space runs hot, and asking for them cost the whole screen its
  // first paint: navigator.storage.estimate() is slow on some devices and the
  // export-cache total opens every cached render to stat it. The cards come
  // from data the app already has; the numbers arrive when they arrive.
  const [live, settings] = await Promise.all([
    loadLiveProjects(),
    getSettings(),
  ]);
  const clipsByProject = await getClipsByProject();
  const countsByKind: Record<ProjectKind, number> = {
    video: 0,
    voice: 0,
    text: 0,
  };
  markBoot("library:read");
  for (const project of live) countsByKind[projectKind(project)] += 1;
  return {
    projects: await Promise.all(
      live.map((project) =>
        summarizeProject(project, clipsByProject.get(project.id) ?? []),
      ),
    ),
    countsByKind,
    plus: settings.watermarkRemoved === true,
    tourCardDismissed: settings.tourCardDismissed === true,
  };
}

/**
 * Every project that still exists, in stable slot order.
 *
 * Exiting a project still in its default state (no clips, default name, no
 * music, no body) must leave nothing behind, just like backing out of
 * /project/new. Every exit path lands back home, so such projects are
 * silently deleted before the cards render.
 */
async function loadLiveProjects(): Promise<Project[]> {
  const all = await listProjects();
  const list: Project[] = [];
  for (const project of all) {
    if (
      project.clipIds.length === 0 &&
      (await deleteProjectIfPristine(project.id))
    )
      continue;
    list.push(project);
  }
  // Creation order — cards must not shuffle every time you open a project.
  list.sort((a, b) => a.createdAt - b.createdAt);
  return list;
}

async function summarizeProject(
  project: Project,
  /** Read once for the whole library — see getClipsByProject. */
  found: readonly ClipRecord[],
): Promise<ProjectSummary> {
  const kind = projectKind(project);
  // Text projects never have clips; skipping the read keeps the text case
  // instant no matter how many notes it holds.
  const byId = new Map(found.map((clip) => [clip.id, clip]));
  const clips =
    kind === "text"
      ? []
      : project.clipIds
          .map((clipId) => byId.get(clipId))
          .filter((clip): clip is ClipRecord => clip !== undefined);
  const durationMs = clips.reduce(
    (sum, clip) => sum + effectiveDurationMs(clip),
    0,
  );
  // Prefer any clip with a high-res poster over an older thumbs-only one.
  const withPoster = clips.find((clip) => clip.poster);
  const withThumb = clips.find((clip) => clip.thumbs && clip.thumbs.length > 0);
  const body =
    kind === "video" ? "" : ((await getProjectText(project.id))?.body ?? "");
  return {
    ...project,
    clipCount: clips.length,
    durationMs,
    posterThumb: withPoster?.poster ?? withThumb?.thumbs?.[0] ?? null,
    wordCount: countWords(body),
    textPreview: body.trim().split("\n")[0]?.slice(0, 80) ?? "",
  };
}

/** Back-compat helper for callers that only want the video-era summaries. */
export async function loadHomeProjects(
  kind: ProjectKind = "video",
): Promise<ProjectSummary[]> {
  const [live, clipsByProject] = await Promise.all([
    loadLiveProjects(),
    getClipsByProject(),
  ]);
  return Promise.all(
    live
      .filter((project) => projectKind(project) === kind)
      .map((project) =>
        summarizeProject(project, clipsByProject.get(project.id) ?? []),
      ),
  );
}

/**
 * Fill in what a clip needs but does not have yet: filmstrip thumbs, and the
 * audio peak the previews normalise with.
 *
 * Off the loading path on purpose — see the note in `loadProjectPage`. Two
 * rules keep it cheap:
 *
 * - Nothing is imported unless something actually needs it. Both helpers
 *   short-circuit on an already-hydrated clip, but the IMPORTS do not, and they
 *   pull in the decoder stack.
 * - Pictures are skipped. A still has no video to poster and no audio to
 *   measure, so it failed both every time and stored nothing to say it had
 *   tried — which meant decoding it again on every single open.
 *
 * Serial, like the code it replaces: Android caps concurrent video decoders
 * hard. Returns true when anything changed, so the caller knows to re-read.
 */
export async function hydrateProjectClips(
  clips: readonly ClipRecord[],
): Promise<boolean> {
  const needsThumbs = (clip: ClipRecord) =>
    clipMediaKind(clip) !== "picture" &&
    !(clip.thumbs && clip.thumbs.length > 0 && clip.poster);
  const needsPeak = (clip: ClipRecord) =>
    clipMediaKind(clip) !== "picture" && clip.audioPeak === undefined;

  const pending = clips.filter((clip) => needsThumbs(clip) || needsPeak(clip));
  if (pending.length === 0) return false;

  const { ensureClipThumbs } = await import("./thumbs");
  const { ensureClipAudioPeak } = await import("./clip-audio-peak");
  for (const clip of pending) {
    const withThumbs = needsThumbs(clip) ? await ensureClipThumbs(clip) : clip;
    if (needsPeak(clip)) await ensureClipAudioPeak(withThumbs);
  }
  return true;
}

export async function loadProjectPage(
  projectId: ProjectId,
  /** Kind for a not-yet-created "/project/new" — ignored for stored ones,
   * whose own record is the authority. */
  newProjectKind: ProjectKind = "video",
): Promise<ProjectLoaderData> {
  try {
    // A "new" project lives only in the URL until the first clip is
    // recorded — nothing is persisted, so backing out leaves nothing behind.
    if (projectId === NEW_PROJECT_ID) {
      const [projects, settings, storage] = await Promise.all([
        listProjects(newProjectKind),
        getSettings(),
        estimateStorageSpace(),
      ]);
      const now = Date.now();
      return {
        project: {
          id: NEW_PROJECT_ID,
          name: `Project ${projects.length + 1}`,
          createdAt: now,
          updatedAt: now,
          clipIds: [],
          kind: newProjectKind,
        },
        kind: newProjectKind,
        clips: [],
        text: "",
        audio: null,
        document: { sections: [], elements: [] },
        documentIsStored: false,
        canUndo: false,
        onboardingDismissed: settings.onboardingDismissed,
        watermarkRemoved: settings.watermarkRemoved === true,
        keepWatermark: settings.keepWatermark === true,
        storage,
        locationTaggingEnabled: settings.locationTaggingEnabled === true,
        error: null,
      };
    }

    const [project, clips, audio, text, undo, settings, records] =
      await Promise.all([
        getProject(projectId),
        getClipsForProject(projectId),
        getProjectAudio(projectId),
        getProjectText(projectId),
        getUndoSnapshot(projectId),
        getSettings(),
        getProjectDocumentRecords(projectId),
      ]);
    // Slow on some devices, and it feeds a banner that only appears when space
    // runs hot — the same reason the home screen stopped waiting for it.
    const storage = null;
    if (!project) {
      return {
        project: null,
        kind: newProjectKind,
        clips: [],
        text: "",
        audio: null,
        document: { sections: [], elements: [] },
        documentIsStored: false,
        canUndo: false,
        onboardingDismissed: settings.onboardingDismissed,
        watermarkRemoved: settings.watermarkRemoved === true,
        keepWatermark: settings.keepWatermark === true,
        storage,
        locationTaggingEnabled: settings.locationTaggingEnabled === true,
        error: "Project not found",
      };
    }
    await setLastOpenedProjectId(projectId);
    const kind = projectKind(project);
    // Backfill filmstrip thumbnails and the persisted audio-normalization
    // measurement for clips that lack them. Serially — Android caps
    // concurrent video decoders hard, and this normally touches at most
    // the clip that was just recorded.
    // Lazy: thumbs / clip-audio-peak → export/shared → mediabunny. Home
    // never hits this path. Voice takes have no video track to poster and
    // no per-clip normalization to measure, so they skip it entirely.
    // NOT awaited here. This used to hydrate every clip — generating filmstrip
    // thumbs and measuring an audio peak, serially, decoding each blob — before
    // the loader returned, so the project showed nothing until it finished. It
    // also imported the decoder stack unconditionally, which is the export
    // path's ~675KB of mediabunny, parsed before first paint even when every
    // clip was already hydrated and there was nothing to decode.
    //
    // The page renders from the stored records and calls `hydrateProjectClips`
    // afterwards. A clip without thumbs shows its tile without a filmstrip for
    // a moment, which is a far smaller cost than the whole project waiting.
    const hydrated = clips;
    return {
      project,
      kind,
      clips: hydrated,
      text: text?.body ?? "",
      audio: audio ?? null,
      document: resolveDocument({
        project,
        clips: hydrated,
        sections: records.sections,
        elements: records.elements,
        ...(text ? { text } : {}),
      }),
      documentIsStored: !isLegacyProject(records.sections),
      canUndo: !!undo,
      onboardingDismissed: settings.onboardingDismissed,
      watermarkRemoved: settings.watermarkRemoved === true,
      keepWatermark: settings.keepWatermark === true,
      storage,
      locationTaggingEnabled: settings.locationTaggingEnabled === true,
      error: null,
    };
  } catch (err) {
    return {
      project: null,
      kind: newProjectKind,
      clips: [],
      text: "",
      audio: null,
      document: { sections: [], elements: [] },
      documentIsStored: false,
      canUndo: false,
      onboardingDismissed: true,
      watermarkRemoved: false,
      keepWatermark: false,
      storage: null,
      locationTaggingEnabled: false,
      error: err instanceof Error ? err.message : "Failed to load project",
    };
  }
}

export async function appendRecording(
  projectId: ProjectId,
  input: {
    blob: Blob;
    mimeType: string;
    durationMs: number;
    /** Default trim-out (recordings end their kept range at the release
     * point; the media itself runs a stop-grace longer). */
    trimEndMs?: number;
    width?: number;
    height?: number;
    lat?: number;
    lng?: number;
    locationAccuracyM?: number;
  },
  options?: {
    /** Poster/thumb captured from the live preview at take end — persisting
     * them here means the loader backfill never has to decode the fresh
     * blob behind the live camera (the post-take black flash). */
    capturedThumbs?: GeneratedThumbs | null;
  },
): Promise<ClipRecord> {
  const clip = await addClip({
    projectId,
    blob: input.blob,
    mimeType: input.mimeType,
    durationMs: input.durationMs,
    trimEndMs: input.trimEndMs,
    width: input.width,
    height: input.height,
    lat: input.lat,
    lng: input.lng,
    locationAccuracyM: input.locationAccuracyM,
  });
  if (options?.capturedThumbs) {
    // Best-effort: on a (rare) persistence failure the loader backfill
    // still generates thumbs — one black flash beats missing artwork. The
    // returned record is NOT decorated in memory: callers revalidate from
    // the store, and claiming unsaved thumbs would mask exactly that
    // fallback.
    await updateClipThumbs(clip.id, options.capturedThumbs).catch(
      () => undefined,
    );
  }
  await clearUndo(projectId);
  // Thumbnails are generated by the loader backfill on the next revalidation.
  return clip;
}

/**
 * Save a finished voice take. Unlike a camera take there is nothing to
 * poster, measure or trim back — the joiner handles the seams — so this is
 * just the blob and its measured length.
 */
export async function appendVoiceTake(
  projectId: ProjectId,
  take: { blob: Blob; mimeType: string; durationMs: number },
): Promise<ClipRecord> {
  const clip = await addClip({
    projectId,
    blob: take.blob,
    mimeType: take.mimeType,
    durationMs: take.durationMs,
  });
  await clearUndo(projectId);
  return clip;
}

/** Persist a project's body copy (a text note, or a voice project's notes). */
export async function saveProjectBody(
  projectId: ProjectId,
  body: string,
): Promise<void> {
  await saveProjectText(projectId, body);
}

/** Notes typed alongside a voice recording, published as the entry body. */
export async function saveVoiceNotes(
  projectId: ProjectId,
  body: string,
): Promise<void> {
  await saveProjectText(projectId, body);
}

export async function removeClip(clipId: ClipId): Promise<void> {
  await deleteClip(clipId);
}

export async function undoLastDelete(
  projectId: ProjectId,
): Promise<ClipRecord | null> {
  return undoDeleteLastClip(projectId);
}

export async function duplicateSelectedClip(
  clipId: ClipId,
): Promise<ClipRecord> {
  return duplicateClip(clipId);
}

export async function moveSelectedClip(
  projectId: ProjectId,
  clipId: ClipId,
  direction: "left" | "right",
): Promise<void> {
  await moveClip(projectId, clipId, direction);
}

export async function trimClip(
  clipId: ClipId,
  trimStartMs: number,
  trimEndMs: number,
): Promise<void> {
  await updateClipTrim(clipId, trimStartMs, trimEndMs);
}

/** Append a picked audio file to the project's background-music playlist. */
export async function addProjectAudioFromFile(
  file: File,
  ensureProjectId: () => Promise<ProjectId>,
): Promise<ProjectAudioRecord> {
  // Probe first: a bad pick on /project/new must not create an empty project.
  const probed = await probeAudioFile(file);
  const projectId = await ensureProjectId();
  return addProjectAudioTrack({
    projectId,
    blob: probed.blob,
    mimeType: probed.mimeType,
    durationMs: probed.durationMs,
    name: probed.name,
  });
}

/**
 * Add a RECORDED take to the project's audio lane, at a position.
 *
 * Distinct from `addProjectAudioFromFile` in three ways, all of them
 * deliberate: it is positioned rather than appended (where the playhead was is
 * the reason someone recorded there), its role is `voice` so it is not ducked
 * under the clip it was recorded to carry, and it needs no probe — the recorder
 * already knows the duration, and probing a blob it just produced would decode
 * it a second time for nothing.
 */
export async function addProjectAudioTake(
  take: { blob: Blob; mimeType: string; durationMs: number },
  input: {
    startMs: number;
    name: string;
    ensureProjectId: () => Promise<ProjectId>;
    /** The scenes it belongs to. Chosen before recording, so always known. */
    span?: { fromElementId: string; toElementId: string } | undefined;
    /** The words it was read from, kept with the take that used them. */
    script?: string | undefined;
  },
): Promise<ProjectAudioRecord> {
  const projectId = await input.ensureProjectId();
  const record = await addProjectAudioTrack({
    projectId,
    blob: take.blob,
    mimeType: take.mimeType,
    durationMs: take.durationMs,
    name: input.name,
    startMs: Math.max(0, input.startMs),
    role: "voice",
  });
  const added = record.tracks[record.tracks.length - 1];
  if (!added) return record;
  if (input.span) await setProjectAudioSpan(projectId, added.id, input.span);
  if (input.script?.trim())
    await setProjectAudioScript(projectId, added.id, input.script);
  return record;
}

/** Move one audio segment on the timeline (null returns it to sequential). */
export async function setAudioTrackStart(
  projectId: ProjectId,
  trackId: string,
  startMs: number | null,
): Promise<void> {
  await updateProjectAudioTrack(projectId, trackId, { startMs });
}

/** Move a lane track to an output position, stating the lane's positions. */
/** Cut a lane track in two at a point in its own time. False = too short. */
export async function splitAudioTrack(
  projectId: ProjectId,
  trackId: string,
  atMs: number,
): Promise<boolean> {
  return splitProjectAudioTrack(projectId, trackId, atMs);
}

export async function removeAudioTrack(
  projectId: ProjectId,
  trackId: string,
): Promise<void> {
  await removeProjectAudioTrack(projectId, trackId);
}

/** Put a removed track back, exactly as it was and where it was. */
export async function restoreAudioTrack(
  projectId: ProjectId,
  track: ProjectAudioTrack,
  index: number,
  fades: { fadeIn: boolean; fadeOut: boolean },
): Promise<void> {
  await restoreProjectAudioTrack(projectId, track, index, fades);
}

/** Re-record one take: new audio, same place, same span, same levels. */
export async function replaceAudioTake(
  projectId: ProjectId,
  trackId: string,
  take: { blob: Blob; mimeType: string; durationMs: number },
): Promise<void> {
  await replaceProjectAudioTake(projectId, trackId, take);
}

/** Store the words one take is read from. */
export async function setAudioScript(
  projectId: ProjectId,
  trackId: string,
  script: string,
): Promise<void> {
  await setProjectAudioScript(projectId, trackId, script);
}

/** Pin a track across a run of scenes, or clear its span. */
export async function setAudioSpan(
  projectId: ProjectId,
  trackId: string,
  span: { fromElementId: string; toElementId: string } | null,
): Promise<void> {
  await setProjectAudioSpan(projectId, trackId, span);
}

/**
 * Move pinned tracks onto their scenes after the film changed.
 *
 * The one place that closes the loop between the document and the lane. Called
 * after anything that could move a scene — an edit, a trim, an insert, a
 * reorder — and a no-op when the lane is free or nothing actually moved.
 */
export async function reconcileAudioSpans(input: {
  projectId: ProjectId;
  document: ProjectDocument;
  clips: readonly ClipRecord[];
  audio: ProjectAudioRecord | null;
  pictureHoldMs?: number | undefined;
}): Promise<boolean> {
  const { audio } = input;
  if (!audio) return false;
  if (!audio.tracks.some((track) => track.spanFromElementId)) return false;
  const plan = planDocument({
    document: input.document,
    clips: input.clips,
    frame: frameForDocument(input.document, input.clips),
    ...(input.pictureHoldMs !== undefined
      ? { pictureHoldMs: input.pictureHoldMs }
      : {}),
  });
  const starts = new Map(
    placeSpans({ plan, tracks: audio.tracks }).map((placement) => [
      placement.trackId,
      placement.startMs,
    ]),
  );
  return writeProjectAudioPositions(input.projectId, starts);
}

/** Update one playlist track's playback settings (trim, level, fades). */
export async function setAudioTrackSettings(
  projectId: ProjectId,
  trackId: string,
  settings: ProjectAudioTrackSettings,
): Promise<void> {
  await updateProjectAudioTrack(projectId, trackId, settings);
}

/** Set a clip's volume levels (1 or null clears the stored override). */
export async function setClipVolumes(
  clipId: ClipId,
  volumes: ClipVolumeSettings,
): Promise<void> {
  await updateClipVolumes(clipId, volumes);
}

export {
  DEVICE_CLIP_ACCEPT,
  importDeviceClips,
  type DeviceClipImportResult,
} from "./import-device-clips";
